import {
  CONTEXT_OPTIONS,
  Choice,
  HINTS,
  LEVEL_OPTIONS,
  MEANS_CHOICES,
  MEANS_ROWS,
  MODALITY_OPTIONS,
  MODULE_CHOICES,
  MODULE_ROWS,
  PREREQUISITES_OPTIONS,
  PSH_OPTIONS,
  QUESTIONS,
  isRequired,
} from "@/constants/preformatifDiagnostic";
import {
  PreformatifDiagnosticAnswers,
  PreformatifDiagnosticHeader,
} from "@/interfaces/PreformatifDiagnostic";
import { FC, ReactNode, useId } from "react";
import { Form, Table } from "react-bootstrap";

type Field = keyof PreformatifDiagnosticAnswers;
type TextField = {
  [K in Field]: PreformatifDiagnosticAnswers[K] extends string ? K : never;
}[Field];

interface Props {
  answers: PreformatifDiagnosticAnswers;
  onChange: (answers: PreformatifDiagnosticAnswers) => void;
  header?: PreformatifDiagnosticHeader;
  /** Champs signalés par l'API comme manquants à la finalisation. */
  missing?: string[];
}

/** Intitulé en gras souligné d'un filet, consigne en italique à droite, comme le PDF. */
const Question: FC<{
  titleId: string;
  title: string;
  hint?: string;
  required: boolean;
  invalid: boolean;
  children: ReactNode;
}> = ({ titleId, title, hint, required, invalid, children }) => (
  <section className="mb-3" aria-labelledby={titleId}>
    <div
      id={titleId}
      className={`fw-bold fs--1 pb-1 border-bottom border-dark ${invalid ? "text-danger" : "text-900"}`}
    >
      {title}
      {required && <span className="text-danger ms-1">*</span>}
    </div>
    {hint && <div className="text-end fst-italic text-600 fs--2">{hint}</div>}
    <div className={hint ? "mt-1" : "mt-2"}>{children}</div>
  </section>
);

/**
 * Le questionnaire tel qu'il s'imprime (export PDF) : mêmes questions, même
 * ordre, mêmes consignes.
 */
const PreformatifDiagnosticForm: FC<Props> = ({
  answers,
  onChange,
  header,
  missing = [],
}) => {
  const id = useId();
  const set = <K extends Field>(
    field: K,
    value: PreformatifDiagnosticAnswers[K],
  ) => onChange({ ...answers, [field]: value });

  const question = (field: Field, children: ReactNode, hint?: string) => (
    <Question
      titleId={`${id}-${field}`}
      title={QUESTIONS[field]}
      hint={hint}
      required={isRequired(field, answers)}
      invalid={missing.includes(field)}
    >
      {children}
    </Question>
  );

  const textArea = (field: TextField) =>
    question(
      field,
      <Form.Control
        as="textarea"
        rows={3}
        aria-labelledby={`${id}-${field}`}
        value={answers[field] as string}
        isInvalid={missing.includes(field)}
        onChange={(event) => set(field, event.target.value)}
      />,
    );

  const checkboxes = (
    field: "context" | "levels" | "modalities",
    options: Choice[],
  ) =>
    question(
      field,
      options.map((option) => (
        <Form.Check
          key={option.key}
          type="checkbox"
          id={`${id}-${field}-${option.key}`}
          className="fs--1"
          label={option.label}
          checked={answers[field].includes(option.key)}
          isInvalid={missing.includes(field)}
          onChange={(event) =>
            set(
              field,
              event.target.checked
                ? options
                    .map((o) => o.key)
                    .filter(
                      (key) =>
                        key === option.key || answers[field].includes(key),
                    )
                : answers[field].filter((key) => key !== option.key),
            )
          }
        />
      )),
      HINTS.multiple,
    );

  const radios = (field: "prerequisites" | "psh", options: Choice[]) =>
    question(
      field,
      options.map((option) => (
        <Form.Check
          key={option.key}
          type="radio"
          name={`${id}-${field}`}
          id={`${id}-${field}-${option.key}`}
          className="fs--1"
          label={option.label}
          checked={answers[field] === option.key}
          isInvalid={missing.includes(field)}
          onChange={() => set(field, option.key)}
        />
      )),
      HINTS.single,
    );

  /** Grille « une case par ligne » : cocher une case décoche l'autre, la recocher l'efface. */
  const grid = (
    field: "modules" | "means",
    rows: Choice[],
    choices: Choice[],
    labelWidth: string,
  ) =>
    question(
      field,
      <Table
        bordered
        size="sm"
        className={`mb-0 fs--1 ${missing.includes(field) ? "border-danger" : ""}`}
      >
        <thead>
          <tr>
            <th style={{ width: labelWidth }} aria-label="Intitulé" />
            {choices.map((choice) => (
              <th key={choice.key} className="text-center fw-normal">
                {choice.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{row.label}</td>
              {choices.map((choice) => (
                <td key={choice.key} className="text-center align-middle">
                  <Form.Check
                    type="checkbox"
                    className="d-inline-block mb-0"
                    aria-label={`${row.label} — ${choice.label}`}
                    checked={answers[field][row.key] === choice.key}
                    onChange={(event) => {
                      const next = { ...answers[field] };
                      if (event.target.checked) next[row.key] = choice.key;
                      else delete next[row.key];
                      set(field, next);
                    }}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>,
      HINTS.grid,
    );

  return (
    <div>
      <p className="text-center text-600 fs--1 mb-3">
        Questionnaire pour managers
      </p>
      {header && (
        <div className="fs--1 mb-3">
          {[
            ["Action de formation", header.action],
            ["Lieu de la formation", header.location],
            ["Date de la formation", header.dates],
            ["Formateur(s)", header.instructors],
          ].map(([label, value]) => (
            <div key={label}>
              <span className="fw-bold">{label} :</span> {value}
            </div>
          ))}
        </div>
      )}

      <div className="bg-200 px-2 py-2 mb-3 d-flex align-items-center gap-2">
        <Form.Label
          htmlFor={`${id}-respondentName`}
          className="mb-0 fs-0 text-nowrap"
        >
          Nom :
          {isRequired("respondentName", answers) && (
            <span className="text-danger ms-1">*</span>
          )}
        </Form.Label>
        <Form.Control
          id={`${id}-respondentName`}
          value={answers.respondentName}
          isInvalid={missing.includes("respondentName")}
          onChange={(event) => set("respondentName", event.target.value)}
        />
      </div>

      {checkboxes("context", CONTEXT_OPTIONS)}
      {textArea("observations")}
      {textArea("objectives")}
      {grid("modules", MODULE_ROWS, MODULE_CHOICES, "30%")}
      {textArea("headcount")}
      {checkboxes("levels", LEVEL_OPTIONS)}
      {textArea("levelObservations")}
      {radios("prerequisites", PREREQUISITES_OPTIONS)}
      {textArea("prerequisitesPrecision")}
      {radios("psh", PSH_OPTIONS)}
      {textArea("pshPrecision")}
      {checkboxes("modalities", MODALITY_OPTIONS)}
      {grid("means", MEANS_ROWS, MEANS_CHOICES, "83%")}
      {textArea("materialComments")}
      {textArea("planningConstraints")}
      {textArea("finalObservations")}
    </div>
  );
};

export default PreformatifDiagnosticForm;
